import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import {
  mediaUrl,
  PORTAL_FONT_PRESETS,
  PORTAL_THEME_MODES,
  TRAINER_COLOR_PRESETS,
  applyPortalThemeMode,
  buildClientHeroTheme,
  heroBackgroundStyle,
} from "../../../utils/clientHeroTheme";
import { trackActivationComplete } from "../../../analytics/metrikaGoals";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import md from "../common/manageDesktop.module.css";
import { Button, useToast } from "../../ui";
import { ActTarget } from "../../../activation";

export default function SettingsPortalBrandingPanel({
  accentColor,
  coverUrl,
  logoUrl,
  fontFamily,
  themeMode: themeModeProp = "light",
  galleryUrls = [],
  fullBranding = true,
  onUpdated,
  compact = false,
  desktop = false,
  rootClassName = "",
}) {
  const toast = useToast();
  const tenantId = useSelector((state) => state.auth.tenant?.id);
  const coverInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [color, setColor] = useState(accentColor || "#6d28d9");
  const [font, setFont] = useState(fontFamily || "inter");
  const [themeMode, setThemeMode] = useState(themeModeProp === "dark" ? "dark" : "light");
  const [cover, setCover] = useState(coverUrl || "");
  const [logo, setLogo] = useState(logoUrl || "");
  const [gallery, setGallery] = useState(galleryUrls || []);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);

  const syncFromProps = (nextAccent, nextCover, nextLogo, nextFont, nextGallery, nextTheme) => {
    if (nextAccent !== undefined) setColor(nextAccent || "#6d28d9");
    if (nextCover !== undefined) setCover(nextCover || "");
    if (nextLogo !== undefined) setLogo(nextLogo || "");
    if (nextFont !== undefined) setFont(nextFont || "inter");
    if (nextGallery !== undefined) setGallery(nextGallery || []);
    if (nextTheme !== undefined) setThemeMode(nextTheme === "dark" ? "dark" : "light");
  };

  useEffect(() => {
    syncFromProps(accentColor, coverUrl, logoUrl, fontFamily, galleryUrls, themeModeProp);
  }, [accentColor, coverUrl, logoUrl, fontFamily, galleryUrls, themeModeProp]);

  const patch = async (fields) => {
    setSaving(true);
    try {
      const res = await clinicAPI.updateTenantSettings(fields);
      const t = res.tenant;
      syncFromProps(
        t.portal_accent_color,
        t.portal_cover_url,
        t.logo_url,
        t.portal_font_family,
        t.portal_gallery,
        t.portal_theme_mode,
      );
      onUpdated?.(t);
      trackActivationComplete("settings_branding", tenantId);
      return t;
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveColor = async (nextColor) => {
    setColor(nextColor);
    const t = await patch({ portal_accent_color: nextColor });
    if (t) toast.success("Цвет сохранён");
  };

  const saveFont = async (nextFont) => {
    setFont(nextFont);
    const t = await patch({ portal_font_family: nextFont });
    if (t) toast.success("Шрифт сохранён");
  };

  const saveThemeMode = async (nextMode) => {
    const prevMode = themeMode;
    setThemeMode(nextMode);
    const t = await patch({ portal_theme_mode: nextMode });
    if (t) {
      applyPortalThemeMode(nextMode);
      toast.success(nextMode === "dark" ? "Тёмная тема включена" : "Светлая тема включена");
    } else {
      setThemeMode(prevMode);
    }
  };

  const uploadCoverFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error("Допустимы JPEG, PNG или WebP");
      return;
    }
    setCoverUploading(true);
    try {
      const res = await clinicAPI.uploadTenantPortalCover(file);
      const raw = res.portal_cover_url || res.tenant?.portal_cover_url;
      const url =
        raw && !/\?v=\d+/.test(raw) ? `${raw}?v=${Date.now()}` : raw;
      setCover(url || "");
      onUpdated?.({
        ...res.tenant,
        portal_cover_url: url || res.tenant?.portal_cover_url || null,
      });
      trackActivationComplete("settings_cover", tenantId);
      toast.success("Обложка установлена");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setCoverUploading(false);
    }
  };

  const onCoverPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    await uploadCoverFile(file);
  };

  const uploadLogoFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error("Допустимы JPEG, PNG или WebP");
      return;
    }
    setLogoUploading(true);
    try {
      const res = await clinicAPI.uploadTenantPortalLogo(file);
      const raw = res.logo_url || res.tenant?.logo_url;
      const url =
        raw && !/\?v=\d+/.test(raw) ? `${raw}?v=${Date.now()}` : raw;
      setLogo(url || "");
      onUpdated?.({
        ...res.tenant,
        logo_url: url || res.tenant?.logo_url || null,
      });
      trackActivationComplete("settings_logo", tenantId);
      toast.success("Аватар клуба установлен");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setLogoUploading(false);
    }
  };

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    await uploadLogoFile(file);
  };

  const removeLogo = async () => {
    const t = await patch({ logo_url: "" });
    if (t) toast.success("Аватар убран");
  };

  const openLogoPicker = () => {
    if (!logoUploading) logoInputRef.current?.click();
  };

  const removeCover = async () => {
    const t = await patch({ portal_cover_url: "" });
    if (t) toast.success("Обложка убрана");
  };

  const previewTheme = buildClientHeroTheme({
    portal_accent_color: color,
    portal_cover_url: cover,
    portal_font_family: font,
  });
  const coverPreview = mediaUrl(cover);
  const logoPreview = mediaUrl(logo);

  const previewStyle = heroBackgroundStyle(previewTheme);

  const openCoverPicker = () => {
    if (!coverUploading) coverInputRef.current?.click();
  };

  const openGalleryPicker = () => {
    if (!galleryUploading) galleryInputRef.current?.click();
  };

  const onGalleryPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error("Допустимы JPEG, PNG или WebP");
      return;
    }
    setGalleryUploading(true);
    try {
      const res = await clinicAPI.uploadTenantPortalGallery(file);
      setGallery(res.portal_gallery || []);
      onUpdated?.(res.tenant);
      toast.success("Фото добавлено в галерею клуба");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setGalleryUploading(false);
    }
  };

  const removeGalleryPhoto = async (url) => {
    const next = gallery.filter((item) => item !== url);
    const t = await patch({ portal_gallery: next });
    if (t) {
      setGallery(next);
      toast.success("Фото удалено");
    }
  };

  const galleryInput = (
    <input
      ref={galleryInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/*"
      hidden
      onChange={onGalleryPick}
    />
  );

  const sectionClass = desktop ? md.desktopPortalSection : s.trainerBrandSection;
  const titleClass = desktop ? md.desktopPortalSectionTitle : s.docSectionTitle;
  const hintClass = desktop ? md.desktopPortalHint : s.trainerBrandHint;
  const actionsClass = desktop ? md.desktopPortalActions : s.trainerBrandCoverActions;

  const themeModeSection = (
    <section className={desktop ? sectionClass : aa.portalBrandBlock}>
      {desktop ? (
        <h3 className={titleClass}>Тема интерфейса</h3>
      ) : (
        <p className={aa.hubSectionLabel}>Тема интерфейса</p>
      )}
      <p className={desktop ? hintClass : aa.hubHint}>
        Светлая или тёмная тема для админов, тренеров и клиентов клуба.
      </p>
      <div className={desktop ? md.desktopPortalThemeGrid : aa.pollSendModeCards}>
        {PORTAL_THEME_MODES.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={
              desktop
                ? themeMode === opt.id
                  ? md.desktopPortalThemeBtnOn
                  : md.desktopPortalThemeBtn
                : themeMode === opt.id
                  ? aa.pollSendModeCardOn
                  : aa.pollSendModeCard
            }
            aria-pressed={themeMode === opt.id}
            onClick={() => saveThemeMode(opt.id)}
            disabled={saving}
          >
            <strong>{opt.label}</strong>
            <small>{opt.hint}</small>
          </button>
        ))}
      </div>
    </section>
  );

  const gallerySection = (
    <section className={desktop ? md.desktopPortalSection : aa.portalBrandBlock}>
      <p className={desktop ? md.desktopPortalSectionTitle : aa.hubSectionLabel}>
        Фото клуба
      </p>
      <p className={hintClass}>
        Слайдер на странице «Клуб» у клиентов. Обложка баннера в слайдер не
        попадает — только логотип и загруженные фото.
      </p>
      {galleryInput}
      <div className={desktop ? md.desktopPortalGalleryGrid : aa.portalGalleryGrid}>
        {gallery.map((url) => (
          <div
            key={url}
            className={desktop ? md.desktopPortalGalleryItem : aa.portalGalleryItem}
          >
            <img src={mediaUrl(url)} alt="" />
            <button
              type="button"
              className={desktop ? md.desktopPortalGalleryRemove : aa.portalGalleryRemove}
              onClick={() => removeGalleryPhoto(url)}
              aria-label="Удалить фото"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className={desktop ? md.desktopPortalGalleryAdd : aa.portalGalleryAdd}
          onClick={openGalleryPicker}
          disabled={galleryUploading}
        >
          {galleryUploading ? "…" : "+ Фото"}
        </button>
      </div>
    </section>
  );

  const coverInput = (
    <input
      ref={coverInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/*"
      hidden
      onChange={onCoverPick}
    />
  );

  const logoInput = (
    <input
      ref={logoInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/*"
      hidden
      onChange={onLogoPick}
    />
  );

  const logoSection = (
    <ActTarget stepId="settings" subId="settings_branding_logo" as="section" className={aa.portalCoverBlock}>
      <p className={aa.hubSectionLabel}>Аватар клуба</p>
      <p className={aa.hubHint}>
        Используется в портале и как иконка при установке приложения на телефон.
      </p>
      {logoInput}
      <div className={aa.portalLogoRow}>
        <button
          type="button"
          className={aa.portalLogoTap}
          onClick={openLogoPicker}
          disabled={logoUploading}
          aria-label={logoPreview ? "Изменить аватар" : "Добавить аватар"}
        >
          {logoPreview ? (
            <img src={logoPreview} alt="" className={aa.portalLogoImg} />
          ) : (
            <span className={aa.portalLogoPlaceholder}>+</span>
          )}
        </button>
        <div className={aa.portalCoverActions}>
          <Button
            type="button"
            block
            className={aa.hubPrimaryCta}
            disabled={logoUploading}
            onClick={openLogoPicker}
          >
            {logoUploading
              ? "Загрузка…"
              : logoPreview
                ? "Заменить аватар"
                : "Загрузить аватар"}
          </Button>
          {logoPreview ? (
            <button
              type="button"
              className={aa.portalCoverRemoveBtn}
              onClick={removeLogo}
              disabled={saving || logoUploading}
            >
              Убрать аватар
            </button>
          ) : (
            <p className={aa.hubHint}>
              Квадратное фото — логотип в шапке и навигации.
            </p>
          )}
        </div>
      </div>
    </ActTarget>
  );

  if (compact) {
    return (
      <div className={aa.portalBrandPanel}>
        <p className={aa.hubHint}>
          {fullBranding
            ? "Фирменный стиль клуба: шапка на главной админа и клиентов, вкладки, кнопки и шрифт. Один раз настроили — видят все сотрудники и клиенты."
            : "Логотип клуба для шапки и навигации."}
        </p>

        {logoSection}

        {fullBranding ? (
          <>
        <ActTarget stepId="settings" subId="settings_branding_cover" as="section" className={aa.portalCoverBlock}>
          <p className={aa.hubSectionLabel}>Фон шапки (обложка)</p>
          {coverInput}
          <button
            type="button"
            className={aa.portalCoverTap}
            style={{ ...previewStyle, fontFamily: previewTheme.fontFamily }}
            onClick={openCoverPicker}
            disabled={coverUploading}
            aria-label={coverPreview ? "Изменить обложку" : "Добавить обложку"}
          >
            <div className={aa.portalBrandPreviewInner}>
              <span className={aa.portalBrandPreviewDate}>Сегодня</span>
              <strong className={aa.portalBrandPreviewTitle}>Привет!</strong>
              <span className={aa.portalBrandPreviewClub}>Ваш клуб</span>
            </div>
            <span className={aa.portalCoverTapBadge}>
              {coverUploading
                ? "Загрузка…"
                : coverPreview
                  ? "Изменить фото"
                  : "+ Добавить фото"}
            </span>
          </button>
          <div className={aa.portalCoverActions}>
            <Button
              type="button"
              block
              className={aa.hubPrimaryCta}
              disabled={coverUploading}
              onClick={openCoverPicker}
            >
              {coverUploading
                ? "Загрузка…"
                : coverPreview
                  ? "Заменить обложку"
                  : "Загрузить фото обложки"}
            </Button>
            {coverPreview ? (
              <button
                type="button"
                className={aa.portalCoverRemoveBtn}
                onClick={removeCover}
                disabled={saving || coverUploading}
              >
                Убрать обложку — останется цветной фон
              </button>
            ) : (
              <p className={aa.hubHint}>
                Горизонтальное фото 3:2 лучше всего смотрится на главной. Без
                фото используется выбранный цвет ниже.
              </p>
            )}
          </div>
        </ActTarget>

        {gallerySection}

        <ActTarget stepId="settings" subId="settings_branding_accent" as="section" className={aa.portalBrandBlock}>
          <p className={aa.hubSectionLabel}>Фирменный цвет</p>
          <p className={aa.hubHint}>
            Кнопки, вкладки и градиент шапки. С обложкой — акцент на элементах
            интерфейса.
          </p>
          <div className={aa.portalColorGrid}>
            {TRAINER_COLOR_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`${aa.portalColorChip} ${
                  color === preset.color ? aa.portalColorChipOn : ""
                }`}
                style={{ "--chip-color": preset.color }}
                onClick={() => saveColor(preset.color)}
                disabled={saving}
                aria-label={preset.label}
                title={preset.label}
              >
                <span className={aa.portalColorChipDot} aria-hidden />
              </button>
            ))}
          </div>
          <label className={aa.portalColorCustom}>
            <span>Свой цвет</span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              onBlur={() => saveColor(color)}
            />
            <code>{color}</code>
          </label>
          <p className={aa.hubSectionLabel} style={{ marginTop: 16 }}>Шрифт</p>
          <div className={aa.portalFontGrid}>
            {PORTAL_FONT_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`${aa.portalFontBtn} ${
                  font === preset.id ? aa.portalFontBtnOn : ""
                }`}
                style={{ fontFamily: preset.family }}
                onClick={() => saveFont(preset.id)}
                disabled={saving}
              >
                {preset.label}
                <span className={aa.portalFontSample}>Аа Бб 123</span>
              </button>
            ))}
          </div>
        </ActTarget>

        <ActTarget stepId="settings" subId="settings_branding_theme" as="div">
        {themeModeSection}
        </ActTarget>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`${desktop ? "" : s.trainerBrandPage} ${rootClassName}`.trim()}>
      <p className={desktop ? md.desktopPortalIntro : s.trainerBrandHint}>
        {fullBranding
          ? "Единый стиль клуба: шапка главной у админов и клиентов, фирменный цвет (вкладки и кнопки), шрифт. Изменения применяются сразу после сохранения."
          : "Логотип клуба для шапки и навигации."}
      </p>

      <ActTarget stepId="settings" subId="settings_branding_logo" as="section" className={sectionClass}>
        <h3 className={titleClass}>Аватар клуба</h3>
        <p className={hintClass}>
          Логотип клуба в шапке и навигации. Рекомендуем квадратное изображение.
        </p>
        {logoInput}
        <div className={desktop ? md.desktopPortalLogoRow : aa.portalLogoRow}>
          <button
            type="button"
            className={aa.portalLogoTap}
            onClick={openLogoPicker}
            disabled={logoUploading}
          >
            {logoPreview ? (
              <img src={logoPreview} alt="" className={aa.portalLogoImg} />
            ) : (
              <span className={aa.portalLogoPlaceholder}>+</span>
            )}
          </button>
          <div className={actionsClass}>
            <Button type="button" onClick={openLogoPicker} disabled={logoUploading}>
              {logoUploading
                ? "Загрузка…"
                : logoPreview
                  ? "Заменить"
                  : "Загрузить аватар"}
            </Button>
            {logoPreview ? (
              <Button
                type="button"
                variant="ghost"
                onClick={removeLogo}
                disabled={saving}
              >
                Убрать
              </Button>
            ) : null}
          </div>
        </div>
      </ActTarget>

      {fullBranding ? (
        <>
      <ActTarget stepId="settings" subId="settings_branding_cover" as="section" className={sectionClass}>
        <h3 className={titleClass}>Обложка главной</h3>
        <p className={hintClass}>
          Фото вместо цветного градиента. Рекомендуем горизонтальное изображение 3:2.
        </p>
        {coverInput}
        <div
          className={`${
            desktop ? md.desktopPortalPreview : s.trainerBrandCoverPreview
          } ${aa.portalBrandPreview}`}
          style={{ ...previewStyle, fontFamily: previewTheme.fontFamily }}
          role="presentation"
        >
          <div className={aa.portalBrandPreviewInner}>
            <span className={aa.portalBrandPreviewDate}>Сегодня</span>
            <strong className={aa.portalBrandPreviewTitle}>Привет!</strong>
            <span className={aa.portalBrandPreviewClub}>Ваш клуб</span>
          </div>
        </div>
        <div className={actionsClass}>
          <Button
            type="button"
            size="sm"
            onClick={openCoverPicker}
            disabled={coverUploading}
          >
            {coverUploading
              ? "Загрузка…"
              : coverPreview
                ? "Заменить фото"
                : "Загрузить обложку"}
          </Button>
          {coverPreview ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={removeCover}
              disabled={saving}
            >
              Убрать
            </Button>
          ) : null}
        </div>
      </ActTarget>

      {gallerySection}

      <ActTarget stepId="settings" subId="settings_branding_accent" as="section" className={sectionClass}>
        <h3 className={titleClass}>Фирменный цвет</h3>
        <p className={hintClass}>
          Вкладки, кнопки и градиент шапки (если нет фото-обложки).
        </p>
        {desktop ? (
          <>
            <div className={md.desktopPortalColorGrid}>
              {TRAINER_COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`${md.desktopPortalColorChip} ${
                    color === preset.color ? md.desktopPortalColorChipOn : ""
                  }`}
                  style={{ "--chip-color": preset.color }}
                  onClick={() => saveColor(preset.color)}
                  disabled={saving}
                  aria-label={preset.label}
                  title={preset.label}
                />
              ))}
            </div>
            <label className={md.desktopPortalColorCustom}>
              <span>Свой цвет</span>
              <input
                id="club-portal-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                onBlur={() => saveColor(color)}
              />
              <code>{color}</code>
            </label>
          </>
        ) : (
          <>
            <div className={s.trainerBrandColors}>
              {TRAINER_COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`${s.trainerBrandColorBtn} ${
                    color === preset.color ? s.trainerBrandColorBtnOn : ""
                  }`}
                  style={{ "--brand-color": preset.color }}
                  onClick={() => saveColor(preset.color)}
                  disabled={saving}
                >
                  <span className={s.trainerBrandColorDot} aria-hidden />
                  {preset.label}
                </button>
              ))}
            </div>
            <div className={s.trainerBrandCustomColor}>
              <label htmlFor="club-portal-color">Свой цвет</label>
              <input
                id="club-portal-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                onBlur={() => saveColor(color)}
              />
              <code>{color}</code>
            </div>
          </>
        )}
        <h3 className={titleClass} style={{ marginTop: 20 }}>Шрифт приложения</h3>
        <p className={hintClass}>Текст на главной и в интерфейсе клиентского портала.</p>
        <div className={desktop ? md.desktopPortalFontGrid : aa.portalFontGrid}>
          {PORTAL_FONT_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`${
                desktop ? md.desktopPortalFontBtn : aa.portalFontBtn
              } ${font === preset.id ? (desktop ? md.desktopPortalFontBtnOn : aa.portalFontBtnOn) : ""}`}
              style={{ fontFamily: preset.family }}
              onClick={() => saveFont(preset.id)}
              disabled={saving}
            >
              {preset.label}
              <span className={desktop ? md.desktopPortalFontSample : aa.portalFontSample}>
                Аа Бб 123
              </span>
            </button>
          ))}
        </div>
      </ActTarget>

      <ActTarget stepId="settings" subId="settings_branding_theme" as="div">
      {themeModeSection}
      </ActTarget>
        </>
      ) : null}
    </div>
  );
}
